import { Suspense, useState, useEffect } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { useNetworkQuerySync } from "./hooks/useNetworkQuerySync";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ClerkProvider, useUser } from "@clerk/clerk-react";
import { setSentryUser, clearSentryUser } from "@/lib/sentryHelpers";
import { OfflineBanner } from "./components/OfflineBanner";
import { useAppLifecycle } from "./hooks/useAppLifecycle";
import { useNativeUI } from "./hooks/useNativeUI";
import { useAppPrefetch } from "./hooks/useAppPrefetch";
import { useOneSignal } from "./hooks/useOneSignal";
import { loadOneSignalDeferred } from "@/lib/onesignalLoader";
import { useOneSignalNative } from "./hooks/useOneSignalNative";
import { captureReferralFromUrl } from "./lib/referralStorage";
import NativePageTransition from "./components/NativePageTransition";
import NativeSplashScreen from "./components/native/SplashScreen";
import { isNative } from "./lib/platform";
import { clerkAppearance } from "./lib/clerkAppearance";
import { useClerkNativeCallback } from "./hooks/useClerkNativeCallback";
import { useRpcGuard } from "./hooks/useRpcGuard";
import { trackNativeLaunch } from "./lib/apkDownload";
import BrandedPageLoader from "./components/BrandedPageLoader";
import InstallPrompt from "./components/InstallPrompt";
import FloatingNotesButton from "./components/notes/FloatingNotesButton";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { useMetaPixel } from "./hooks/useMetaPixel";
import OnboardingWhatsAppWelcome from "./components/OnboardingWhatsAppWelcome";

// Lazy load non-critical pages for code splitting
const Index = lazyWithRetry(() => import("./pages/Index"));
const Dashboard = lazyWithRetry(() => import("./pages/DashboardNew"));
const AdminAuth = lazyWithRetry(() => import("./pages/AdminAuth"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const CountrySelection = lazyWithRetry(() => import("./pages/CountrySelection"));
const OnboardingWizard = lazyWithRetry(() => import("./pages/OnboardingWizard"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazyWithRetry(() => import("./pages/TermsAndConditions"));
const VerificationDemo = lazyWithRetry(() => import("./pages/VerificationDemo"));
const PLMatches = lazyWithRetry(() => import("./pages/PLMatches"));
const PLStandings = lazyWithRetry(() => import("./pages/PLStandings"));
const PLMatchDetail = lazyWithRetry(() => import("./pages/PLMatchDetail"));
const PLScorers = lazyWithRetry(() => import("./pages/PLScorers"));
const PLNews = lazyWithRetry(() => import("./pages/PLNews"));
const PLNewsArticle = lazyWithRetry(() => import("./pages/PLNewsArticle"));
const PLVideos = lazyWithRetry(() => import("./pages/PLVideos"));
const PLVideoPlayer = lazyWithRetry(() => import("./pages/PLVideoPlayer"));
const FPLHub = lazyWithRetry(() => import("./pages/FPLHub"));
import FormatGate from "./components/competitions/FormatGate";
const H2HArena = lazyWithRetry(() => import("./pages/H2HArena"));
const TagTeamArena = lazyWithRetry(() => import("./pages/TagTeamArena"));
const KnockoutArena = lazyWithRetry(() => import("./pages/KnockoutArena"));
const ChampionsLeagueArena = lazyWithRetry(() => import("./pages/ChampionsLeagueArena"));
const InfluencerDashboard = lazyWithRetry(() => import("./pages/InfluencerDashboard"));
const HostDashboard = lazyWithRetry(() => import("./pages/HostDashboard"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const PublicManagerArticles = lazyWithRetry(() => import("./pages/PublicManagerArticles"));

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY environment variable");
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24h - required for persistence
      staleTime: 1000 * 60 * 2,
      refetchOnWindowFocus: false,
    },
  },
});

const persister =
  typeof window !== "undefined"
    ? createSyncStoragePersister({ storage: window.localStorage, key: "ffa-rq-cache" })
    : undefined;

// Capture referral code from URL on module load (web)
captureReferralFromUrl(window.location.href);

// Branded loading fallback for route transitions
const PageLoader = () => <BrandedPageLoader />;

// Inner component that uses hooks requiring Router context
function AppRoutes() {
  const { isSignedIn, user } = useUser();

  // Attach Clerk identity to all Sentry events (no extra fetches).
  useEffect(() => {
    if (isSignedIn && user?.id) {
      setSentryUser({
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress ?? undefined,
      });
    } else {
      clearSentryUser();
    }
  }, [isSignedIn, user?.id, user?.primaryEmailAddress?.emailAddress]);

  useAppLifecycle();
  useNativeUI();
  useAppPrefetch();
  useNetworkQuerySync();
  useEffect(() => {
    loadOneSignalDeferred();
  }, []);
  useOneSignal();
  useOneSignalNative();
  useRpcGuard();
  useClerkNativeCallback();
  useMetaPixel();

  // Native install / active-device analytics (Android APK distribution).
  useEffect(() => {
    if (!isNative) return;
    const t = setTimeout(() => {
      void trackNativeLaunch();
    }, 4000);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <OfflineBanner />
      <NativePageTransition>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/dashboard" element={<><OnboardingWhatsAppWelcome /><Dashboard /></>} />
            <Route path="/onboarding/country-selection" element={<CountrySelection />} />
            <Route path="/onboarding/profile-setup" element={<OnboardingWizard />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/verification-demo" element={<VerificationDemo />} />
            <Route path="/admin-auth" element={<AdminAuth />} />
            <Route path="/admin" element={<AdminDashboard />} />
            {/* Premier League Hub Routes */}
            <Route path="/premier-league/matches" element={<PLMatches />} />
            <Route path="/premier-league/matches/:fixtureId" element={<PLMatchDetail />} />
            <Route path="/premier-league/standings" element={<PLStandings />} />
            <Route path="/premier-league/scorers" element={<PLScorers />} />
            <Route path="/premier-league/news" element={<PLNews />} />
            <Route path="/premier-league/news/:slug" element={<PLNewsArticle />} />
            <Route path="/premier-league/videos" element={<PLVideos />} />
            <Route path="/premier-league/videos/:slug" element={<PLVideoPlayer />} />
            <Route path="/fpl-hub" element={<FPLHub />} />
            <Route
              path="/h2h-arena"
              element={
                <FormatGate format="H2H" label="Head-to-Head">
                  <H2HArena />
                </FormatGate>
              }
            />
            <Route
              path="/tag-team-arena"
              element={
                <FormatGate format="DUO" label="Tag-Team Duos">
                  <TagTeamArena />
                </FormatGate>
              }
            />
            <Route
              path="/knockout-arena"
              element={
                <FormatGate format="KNOCKOUT" label="Knockout Cup">
                  <KnockoutArena />
                </FormatGate>
              }
            />
            <Route
              path="/champions-league"
              element={
                <FormatGate format="CHAMPIONS_LEAGUE" label="FFA Champions League">
                  <ChampionsLeagueArena />
                </FormatGate>
              }
            />

            <Route path="/influencer" element={<InfluencerDashboard />} />
            <Route path="/host-dashboard" element={<HostDashboard />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="/manager-articles" element={<PublicManagerArticles />} />
            <Route path="/manager-articles/:articleId" element={<PublicManagerArticles />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </NativePageTransition>
      <FloatingNotesButton />
      <CookieConsentBanner />
    </>
  );
}

const App = () => {
  const [splashDone, setSplashDone] = useState(!isNative);

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      appearance={clerkAppearance as any}
      allowedRedirectOrigins={[
        "fantasyfootballafrica://",
        "com.fantasyfootballafrica.app://",
        "capacitor://localhost",
        "https://fantasyfootballafrica.com",
      ]}
    >
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister: persister!,
          maxAge: 1000 * 60 * 60 * 24,
          buster: "v1",
        }}
      >
        <TooltipProvider>
          <NativeSplashScreen onComplete={() => setSplashDone(true)} />
          {splashDone && (
            <BrowserRouter>
              <Toaster />
              <InstallPrompt />
              <Sonner />
              <AppRoutes />
            </BrowserRouter>
          )}
        </TooltipProvider>
      </PersistQueryClientProvider>
    </ClerkProvider>
  );
};

export default App;
